import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BedDouble, UserPlus, Pencil, LogOut, MapPin, History, Download } from 'lucide-react';
import { isAdmin as checkIsAdmin, readCurrentUser } from '../lib/permissions';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DISCHARGE_TYPES = [
  'Planned',
  'AMA (Against Medical Advice)',
  'Administrative',
  'Clinical Transfer',
];

// Notify in-app listeners (e.g., Overview KPI card) that census changed.
// We dispatch a custom event so same-tab consumers refresh immediately;
// other tabs/windows can refresh via polling on focus.
const CENSUS_EVENT = 'caredesk:census-changed';
const fireCensusChanged = () => {
  try { window.dispatchEvent(new Event(CENSUS_EVENT)); } catch { /* noop */ }
};

// Public helper for other pages: returns the current census array.
export const fetchCensus = async () => {
  const { data } = await axios.get(`${API_URL}/api/census`);
  return data;
};
export { CENSUS_EVENT };

const daysInProgram = (admittedAt) => {
  if (!admittedAt) return 0;
  const start = new Date(admittedAt + 'T00:00:00');
  const now = new Date();
  const diff = Math.floor((now - start) / (1000 * 60 * 60 * 24));
  return diff < 0 ? 0 : diff;
};

const formatAdmitted = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
};

// ==================== CSV / DOWNLOAD HELPERS ====================
// (Local copy of the pattern in Reports.jsx — kept self-contained here so the
// Census export remains a one-file change. A future refactor can hoist these
// into a shared util.)
const csvEscape = (val) => {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};
const rowsToCSV = (rows) => rows.map((row) => row.map(csvEscape).join(',')).join('\r\n');
const triggerDownload = (text, filename, mime = 'text/csv;charset=utf-8;') => {
  const blob = new Blob(['\uFEFF' + text], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const Census = () => {
  const admin = checkIsAdmin(readCurrentUser());
  const [beds, setBeds] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { mode: 'edit'|'admit', bed }
  const [discharging, setDischarging] = useState(null); // bed object being discharged
  const [dateRange, setDateRange] = useState('all'); // 'all' | '30d' | '90d' | 'qtd' | 'ytd'

  const refresh = async () => {
    // Fetch beds and history independently so a failure in one (e.g. a stale
    // backend that doesn't yet have /api/census/history) cannot blank out the
    // entire page. Beds are critical; history degrades gracefully to empty.
    // Discharge history is an admin-only tool — staff never request it.
    const [bedsRes, historyRes] = await Promise.allSettled([
      axios.get(`${API_URL}/api/census`),
      admin ? axios.get(`${API_URL}/api/census/history`) : Promise.resolve({ data: [] }),
    ]);
    if (bedsRes.status === 'fulfilled') {
      setBeds(bedsRes.value.data || []);
    } else {
      console.error('Failed to fetch census', bedsRes.reason);
    }
    if (historyRes.status === 'fulfilled') {
      setHistory(historyRes.value.data || []);
    } else {
      console.warn('Discharge history unavailable (backend may be stale):', historyRes.reason);
      setHistory([]);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { await refresh(); } finally { setLoading(false); }
    })();
  }, []);

  const occupiedCount = useMemo(() => beds.filter((b) => b.occupied).length, [beds]);
  const totalBeds = beds.length;
  const censusPct = totalBeds > 0 ? Math.round((occupiedCount / totalBeds) * 100) : 0;

  // Date-range filter for the discharge history table + exports. Computed
  // once per (history, dateRange) change so both the table render and the
  // CSV/PDF builders see the exact same filtered set.
  const visibleHistory = useMemo(() => {
    if (dateRange === 'all') return history;
    const now = new Date();
    let from;
    if (dateRange === '30d') { from = new Date(now); from.setDate(now.getDate() - 30); }
    else if (dateRange === '90d') { from = new Date(now); from.setDate(now.getDate() - 90); }
    else if (dateRange === 'qtd') { from = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1); }
    else if (dateRange === 'ytd') { from = new Date(now.getFullYear(), 0, 1); }
    const fromMs = from.getTime();
    return history.filter((h) => {
      const t = h.dischargeDate ? new Date(h.dischargeDate).getTime() : NaN;
      return Number.isFinite(t) && t >= fromMs;
    });
  }, [history, dateRange]);

  const unit2 = beds.filter((b) => b.unit === 'Unit 2');
  const unit3 = beds.filter((b) => b.unit === 'Unit 3');

  // Open the discharge dialog (requires dischargedBy + dischargeType)
  const handleDischarge = (bed) => {
    setDischarging(bed);
  };

  const handleConfirmDischarge = async ({ dischargedBy, dischargeType }) => {
    if (!discharging) return;
    await axios.post(`${API_URL}/api/census/discharge/${discharging.bed}`, {
      dischargedBy,
      dischargeType,
    });
    fireCensusChanged();
    setDischarging(null);
    await refresh();
  };

  const handleSave = async (updated) => {
    await axios.put(`${API_URL}/api/census/${updated.bed}`, { ...updated, occupied: true });
    fireCensusChanged();
    setEditing(null);
    await refresh();
  };

  const handleDischargeExportCSV = () => {
    const today = new Date().toISOString().slice(0, 10);
    triggerDownload(
      rowsToCSV(buildDischargeCSVRows(visibleHistory)),
      `caredesk-discharge-history-${dateRange}-${today}.csv`,
    );
  };

  const handleDischargeExportPDF = () => {
    const w = window.open('', '_blank', 'width=1100,height=850');
    if (!w) {
      alert('Pop-up blocked. Please allow pop-ups to export PDF.');
      return;
    }
    w.document.open();
    w.document.write(buildDischargeHistoryHTML(visibleHistory, dateRange));
    w.document.close();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="census-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="census-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Census</h1>
        <p className="text-sm text-muted-foreground mt-1">
          ALIGN 3.1 Supervised Living — bed map &amp; resident roster
        </p>
      </div>

      {/* Summary bar */}
      <Card
        className="p-5 bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30"
        data-testid="census-summary"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-green-500/15 flex items-center justify-center">
            <BedDouble className="h-5 w-5 text-green-700 dark:text-green-400" />
          </div>
          <div>
            <div className="text-xl font-bold text-green-700 dark:text-green-300">
              {occupiedCount} / {totalBeds} Beds Occupied · {censusPct}% Census
            </div>
            <div className="text-xs text-muted-foreground">
              Live count from current bed map
            </div>
          </div>
        </div>
      </Card>

      {/* Two-unit grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UnitColumn
          title="Unit 2"
          address="3 Bridge Street, Highland Springs VA 23075"
          beds={unit2}
          readOnly={!admin}
          onEdit={(bed) => setEditing({ mode: bed.occupied ? 'edit' : 'admit', bed })}
          onDischarge={handleDischarge}
        />
        <UnitColumn
          title="Unit 3"
          address="3 Bridge Street, Highland Springs VA 23075"
          beds={unit3}
          readOnly={!admin}
          onEdit={(bed) => setEditing({ mode: bed.occupied ? 'edit' : 'admit', bed })}
          onDischarge={handleDischarge}
        />
      </div>

      {admin && (
        <>
          <BedDialog
            editing={editing}
            onClose={() => setEditing(null)}
            onSave={handleSave}
          />

          <DischargeDialog
            bed={discharging}
            onClose={() => setDischarging(null)}
            onConfirm={handleConfirmDischarge}
          />

          <DischargeHistoryCard
            history={history}
            visibleHistory={visibleHistory}
            dateRange={dateRange}
            setDateRange={setDateRange}
            onExportCSV={handleDischargeExportCSV}
            onExportPDF={handleDischargeExportPDF}
          />
        </>
      )}
    </div>
  );
};

// ==================== UNIT COLUMN ====================
const UnitColumn = ({ title, address, beds, readOnly, onEdit, onDischarge }) => (
  <div className="space-y-3">
    <div>
      <h2 className="text-base font-semibold flex items-center gap-2">
        {title}
        <Badge variant="secondary" className="text-[10px]">
          {beds.filter((b) => b.occupied).length} / {beds.length}
        </Badge>
      </h2>
      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
        <MapPin className="h-3 w-3" />
        {address}
      </p>
    </div>
    <div className="space-y-3">
      {beds.map((bed) => (
        <BedCard key={bed.bed} bed={bed} readOnly={readOnly} onEdit={onEdit} onDischarge={onDischarge} />
      ))}
    </div>
  </div>
);

// ==================== BED CARD ====================
const BedCard = ({ bed, readOnly, onEdit, onDischarge }) => {
  const occupied = bed.occupied;
  return (
    <Card
      className={`p-4 ${occupied ? '' : 'bg-muted/40'}`}
      data-testid={`bed-card-${bed.bed}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-bold tracking-wide">Bed {bed.bed}</span>
            {occupied ? (
              <Badge variant="success">Occupied</Badge>
            ) : (
              <Badge variant="destructive">Open</Badge>
            )}
          </div>

          {occupied ? (
            <>
              <h3 className="text-base font-semibold leading-tight">{bed.clientName}</h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                <div>
                  <span className="font-medium text-foreground/70">ID</span>{' '}
                  <span className="font-mono">{bed.clientId}</span>
                </div>
                <div>
                  <span className="font-medium text-foreground/70">Med Box</span>{' '}
                  <span className="font-mono">{bed.medBox}</span>
                </div>
                <div>
                  <span className="font-medium text-foreground/70">Admitted</span>{' '}
                  {formatAdmitted(bed.admittedAt)}
                </div>
                <div>
                  <span className="font-medium text-foreground/70">Days</span>{' '}
                  <span className="font-semibold text-foreground">{daysInProgram(bed.admittedAt)}</span>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              Bed available — admit a new resident
            </p>
          )}
        </div>
      </div>

      {!readOnly && (
        <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t">
          {occupied ? (
            <>
              <Button size="sm" variant="outline" onClick={() => onEdit(bed)} data-testid={`edit-bed-${bed.bed}`}>
                <Pencil className="h-3.5 w-3.5 mr-1" />
                Edit
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onDischarge(bed)} data-testid={`discharge-bed-${bed.bed}`}>
                <LogOut className="h-3.5 w-3.5 mr-1" />
                Discharge
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={() => onEdit(bed)} data-testid={`admit-bed-${bed.bed}`}>
              <UserPlus className="h-3.5 w-3.5 mr-1" />
              Admit
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};

// ==================== EDIT DIALOG ====================
const BedDialog = ({ editing, onClose, onSave }) => {
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (editing?.bed) {
      setDraft({
        bed: editing.bed.bed,
        unit: editing.bed.unit,
        clientName: editing.bed.clientName || '',
        clientId: editing.bed.clientId || '',
        dateOfBirth: editing.bed.dateOfBirth || '',
        medicaidNumber: editing.bed.medicaidNumber || '',
        admittedAt: editing.bed.admittedAt || new Date().toISOString().slice(0, 10),
        medBox: editing.bed.medBox || '',
      });
    } else {
      setDraft(null);
    }
  }, [editing]);

  if (!editing || !draft) return null;
  const isAdmit = editing.mode === 'admit';
  const setField = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="bed-dialog">
        <DialogHeader>
          <DialogTitle>
            {isAdmit ? 'Admit Resident' : 'Edit Resident'} — Bed {draft.bed}
          </DialogTitle>
          <DialogDescription>{draft.unit}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Field label="Client Name">
            <Input value={draft.clientName} onChange={setField('clientName')} data-testid="bed-input-name" />
          </Field>
          <Field label="Client ID">
            <Input value={draft.clientId} onChange={setField('clientId')} className="font-mono" />
          </Field>
          <Field label="Date of Birth">
            <Input
              type="date"
              value={draft.dateOfBirth}
              onChange={setField('dateOfBirth')}
              data-testid="bed-input-dob"
            />
          </Field>
          <Field label="Medicaid Number">
            <Input
              value={draft.medicaidNumber}
              onChange={setField('medicaidNumber')}
              className="font-mono"
              data-testid="bed-input-medicaid"
            />
          </Field>
          <Field label="Admission Date">
            <Input type="date" value={draft.admittedAt} onChange={setField('admittedAt')} />
          </Field>
          <Field label="Med Box">
            <Input value={draft.medBox} onChange={setField('medBox')} className="font-mono" />
          </Field>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(draft)} data-testid="bed-save-button">
            {isAdmit ? 'Admit' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

// ==================== DISCHARGE DIALOG ====================
const DischargeDialog = ({ bed, onClose, onConfirm }) => {
  const [dischargedBy, setDischargedBy] = useState('');
  const [dischargeType, setDischargeType] = useState('Planned');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset state whenever a new bed is opened/closed
  useEffect(() => {
    setDischargedBy('');
    setDischargeType('Planned');
    setError('');
    setSubmitting(false);
  }, [bed]);

  if (!bed) return null;

  const handleConfirm = async () => {
    if (!dischargedBy.trim()) {
      setError('Please enter the staff name processing this discharge.');
      return;
    }
    if (!dischargeType) {
      setError('Please choose a discharge type.');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      await onConfirm({ dischargedBy: dischargedBy.trim(), dischargeType });
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || 'Discharge failed. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!bed} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="discharge-dialog">
        <DialogHeader>
          <DialogTitle>Discharge Resident — Bed {bed.bed}</DialogTitle>
          <DialogDescription>
            {bed.unit} · {bed.clientName || 'Unnamed resident'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Field label="Discharged By (staff processing this discharge)">
            <Input
              value={dischargedBy}
              onChange={(e) => setDischargedBy(e.target.value)}
              placeholder="e.g. Brittany Chandler"
              data-testid="discharge-by-input"
              autoFocus
            />
          </Field>
          <Field label="Discharge Type">
            <select
              value={dischargeType}
              onChange={(e) => setDischargeType(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="discharge-type-select"
            >
              {DISCHARGE_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>
          {error && (
            <p className="text-xs text-destructive" data-testid="discharge-error">{error}</p>
          )}
          <p className="text-xs text-muted-foreground">
            A history record will be saved before the bed is set to Open.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting}
            data-testid="discharge-confirm-button"
          >
            {submitting ? 'Discharging…' : 'Confirm Discharge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== DISCHARGE HISTORY ====================
const formatHistoryDate = (iso) => {
  if (!iso) return '—';
  // Accept either a full ISO timestamp ("2026-05-10T04:17:30...") or a plain "YYYY-MM-DD".
  const d = iso.includes('T') ? new Date(iso) : new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
};

// Build the row-set for a CSV export of the discharge history table.
// Header + one row per record; covers every on-screen column plus
// medicaidNumber and medBoxCode which compliance audits need.
const buildDischargeCSVRows = (history) => {
  const header = [
    'Discharge Date',
    'Client Name',
    'Client ID',
    'Medicaid #',
    'Unit',
    'Bed',
    'Med Box Code',
    'Admit Date',
    'Length of Stay (days)',
    'Discharged By',
    'Discharge Type',
  ];
  const rows = history.map((h) => [
    h.dischargeDate ? formatHistoryDate(h.dischargeDate) : '',
    h.clientName || '',
    h.clientId || '',
    h.medicaidNumber || '',
    h.unit || '',
    h.bed || '',
    h.medBoxCode || '',
    h.admitDate ? formatHistoryDate(h.admitDate) : '',
    h.lengthOfStay ?? '',
    h.dischargedBy || '',
    h.dischargeType || '',
  ]);
  return [header, ...rows];
};

// Self-contained printable HTML for the "Export as PDF" path. Opens in a popup
// window which immediately fires window.print(); the user saves as PDF via the
// browser's print dialog (same pattern used by ShiftNotes.jsx).
const RANGE_LABELS = {
  all: 'All Time',
  '30d': 'Last 30 Days',
  '90d': 'Last 90 Days',
  qtd: 'This Quarter',
  ytd: 'Year to Date',
};
const buildDischargeHistoryHTML = (history, range = 'all') => {
  const label = RANGE_LABELS[range] || 'All Time';
  const rows = history
    .map(
      (h) => `
    <tr>
      <td>${h.dischargeDate ? formatHistoryDate(h.dischargeDate) : ''}</td>
      <td>${h.clientName || ''}</td>
      <td>${h.clientId || ''}</td>
      <td>${h.medicaidNumber || ''}</td>
      <td>${h.unit || ''}</td>
      <td>${h.bed || ''}</td>
      <td>${h.admitDate ? formatHistoryDate(h.admitDate) : ''}</td>
      <td>${h.lengthOfStay ?? ''}</td>
      <td>${h.dischargedBy || ''}</td>
      <td>${h.dischargeType || ''}</td>
    </tr>`,
    )
    .join('');
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"/>
    <title>CareDesk — Discharge History</title>
    <style>
      @page { size: 11in 8.5in landscape; margin: 0.5in; }
      @page { @bottom-right { content: "Page " counter(page) " of " counter(pages); font-size: 9pt; color: #444; font-family: Arial, sans-serif; } }
      body { font-family: Arial, sans-serif; font-size: 9pt; }
      h1 { font-size: 13pt; margin-bottom: 4px; }
      p.sub { font-size: 9pt; color: #666; margin: 0 0 12px; }
      table { width: 100%; border-collapse: collapse; }
      th { background: #f0f0f0; text-align: left; padding: 5px 6px; font-size: 8pt; border-bottom: 1.5px solid #999; }
      td { padding: 4px 6px; border-bottom: 1px solid #ddd; vertical-align: top; }
      tr { page-break-inside: avoid; break-inside: avoid; }
    </style></head><body>
    <h1>Discharge History</h1>
    <p class="sub">ALIGN Inc. · ASAM 3.1 Residential Program · ${label} · Exported ${new Date().toLocaleDateString()}</p>
    <table>
      <thead><tr>
        <th>Discharge Date</th><th>Client Name</th><th>Client ID</th><th>Medicaid #</th>
        <th>Unit</th><th>Bed</th><th>Admit Date</th><th>LOS (days)</th>
        <th>Discharged By</th><th>Type</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <script>window.addEventListener('load',function(){ setTimeout(function(){ window.print(); },300); });</script>
    </body></html>`;
};

const DischargeHistoryCard = ({
  history,
  visibleHistory,
  dateRange,
  setDateRange,
  onExportCSV,
  onExportPDF,
}) => (
  <Card className="p-0 overflow-hidden" data-testid="discharge-history-card">
    <div className="flex items-center gap-2 px-5 py-4 border-b">
      <History className="h-4 w-4 text-muted-foreground" />
      <h2 className="text-base font-semibold">Discharge History</h2>
      <Select value={dateRange} onValueChange={setDateRange}>
        <SelectTrigger
          className="h-8 w-[140px] text-xs"
          data-testid="discharge-history-range-select"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" data-testid="range-option-all">All time</SelectItem>
          <SelectItem value="30d" data-testid="range-option-30d">Last 30 days</SelectItem>
          <SelectItem value="90d" data-testid="range-option-90d">Last 90 days</SelectItem>
          <SelectItem value="qtd" data-testid="range-option-qtd">This quarter</SelectItem>
          <SelectItem value="ytd" data-testid="range-option-ytd">Year to date</SelectItem>
        </SelectContent>
      </Select>
      <Badge variant="secondary" className="text-[10px]">
        {dateRange === 'all'
          ? `${history.length} ${history.length === 1 ? 'record' : 'records'}`
          : `${visibleHistory.length} of ${history.length} ${history.length === 1 ? 'record' : 'records'}`}
      </Badge>
      <div className="ml-auto">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={visibleHistory.length === 0}
              data-testid="discharge-history-export-button"
            >
              <Download className="h-3.5 w-3.5 mr-1" /> Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onExportCSV} data-testid="discharge-export-csv">
              Export as CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onExportPDF} data-testid="discharge-export-pdf">
              Export as PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
    {history.length === 0 ? (
      <div
        className="p-8 text-center text-sm text-muted-foreground"
        data-testid="discharge-history-empty"
      >
        No discharge history yet
      </div>
    ) : visibleHistory.length === 0 ? (
      <div
        className="p-8 text-center text-sm text-muted-foreground"
        data-testid="discharge-history-empty-filtered"
      >
        No discharges in this date range.
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="discharge-history-table">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left p-3 font-semibold text-xs uppercase tracking-wide">Discharge Date</th>
              <th className="text-left p-3 font-semibold text-xs uppercase tracking-wide">Client Name</th>
              <th className="text-left p-3 font-semibold text-xs uppercase tracking-wide">Client ID</th>
              <th className="text-left p-3 font-semibold text-xs uppercase tracking-wide">Unit</th>
              <th className="text-left p-3 font-semibold text-xs uppercase tracking-wide">Bed</th>
              <th className="text-left p-3 font-semibold text-xs uppercase tracking-wide">Admit Date</th>
              <th className="text-left p-3 font-semibold text-xs uppercase tracking-wide">Length of Stay</th>
              <th className="text-left p-3 font-semibold text-xs uppercase tracking-wide">Discharged By</th>
              <th className="text-left p-3 font-semibold text-xs uppercase tracking-wide">Type</th>
            </tr>
          </thead>
          <tbody>
            {visibleHistory.map((h) => (
              <tr key={h.id} className="border-t hover:bg-muted/20" data-testid={`history-row-${h.id}`}>
                <td className="p-3 whitespace-nowrap">{formatHistoryDate(h.dischargeDate)}</td>
                <td className="p-3 font-medium">{h.clientName || '—'}</td>
                <td className="p-3 font-mono text-xs">{h.clientId || '—'}</td>
                <td className="p-3">{h.unit || '—'}</td>
                <td className="p-3">{h.bed || '—'}</td>
                <td className="p-3 whitespace-nowrap">{formatHistoryDate(h.admitDate)}</td>
                <td className="p-3">
                  {h.lengthOfStay} {h.lengthOfStay === 1 ? 'day' : 'days'}
                </td>
                <td className="p-3">{h.dischargedBy || '—'}</td>
                <td className="p-3">
                  <Badge variant="outline" className="text-[10px]">{h.dischargeType || '—'}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Card>
);

export default Census;
